import React, { createContext, useContext, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Banner } from './Banner';
import { NotificationBannerManager } from './NotificationBannerManager';
import { useDemo } from './demo/DemoProvider';
import { useNotifications } from '../hooks/useNotifications';
import { useAccess } from './access/AccessProvider';
import { getAthenaUrl } from '../config/platformUrls';
import { MigrationModal, NEW_PLATFORM_URL } from './MigrationModal';

// Roles eligible for Athena trial banner (users with subscriptions getting temporary Athena access)
// Note: user:free is excluded - they have Athena access but no subscription, so the banner doesn't apply
const ATHENA_TRIAL_ROLES = ['user:beta', 'user:paid', 'user:legacy', 'user:grantee'];
const ATHENA_TRIAL_END_DATE = new Date('2026-02-06T00:00:00');

/** Tailwind top offset for the nth stacked banner (0-indexed). */
function bannerTopPosition(index: number): string {
  if (index <= 0) return 'top-0';
  if (index === 1) return 'top-8 sm:top-9';
  if (index === 2) return 'top-16 sm:top-[4.5rem]';
  return 'top-24 sm:top-[6.75rem]';
}

interface BannerContextValue {
  hasMigrationBanner: boolean;
  hasNotificationBanner: boolean;
  hasDemoBanner: boolean;
  hasAthenaTrialBanner: boolean;
  hasAnyBanner: boolean;
  bannerCount: number;
}

const BannerContext = createContext<BannerContextValue>({
  hasMigrationBanner: true,
  hasNotificationBanner: false,
  hasDemoBanner: false,
  hasAthenaTrialBanner: false,
  hasAnyBanner: true,
  bannerCount: 1,
});

export const useBannerState = () => useContext(BannerContext);

interface BannerManagerProps {
  children: React.ReactNode;
}

export function BannerManager({ children }: BannerManagerProps) {
  const { isDemo, setDemo } = useDemo();
  const { hasNotifications } = useNotifications();
  const { access } = useAccess();
  const [athenaTrialDismissed, setAthenaTrialDismissed] = useState(false);
  const [athenaPromoDismissed, setAthenaPromoDismissed] = useState(false);

  // Always show the legacy → platform migration notice
  const hasMigrationBanner = true;

  // Check if user is eligible for Athena trial banner
  const isAthenaTrialEligible =
    access?.role &&
    ATHENA_TRIAL_ROLES.includes(access.role) &&
    new Date() < ATHENA_TRIAL_END_DATE &&
    !athenaTrialDismissed &&
    !isDemo;

  // Check if user is eligible for Athena promo banner (base users)
  const isAthenaPromoEligible = access?.role === 'user:base' && !athenaPromoDismissed && !isDemo;

  const hasNotificationBanner = hasNotifications && !isDemo;

  const bannerState: BannerContextValue = {
    hasMigrationBanner,
    hasNotificationBanner,
    hasDemoBanner: isDemo,
    hasAthenaTrialBanner: !!isAthenaTrialEligible || !!isAthenaPromoEligible,
    hasAnyBanner:
      hasMigrationBanner ||
      hasNotificationBanner ||
      isDemo ||
      !!isAthenaTrialEligible ||
      !!isAthenaPromoEligible,
    bannerCount:
      (hasMigrationBanner ? 1 : 0) +
      (hasNotificationBanner ? 1 : 0) +
      (isDemo ? 1 : 0) +
      (isAthenaTrialEligible ? 1 : 0) +
      (isAthenaPromoEligible ? 1 : 0),
  };

  // Stack order: migration → notifications → demo → athena
  let nextBannerIndex = 0;
  const migrationPosition = bannerTopPosition(nextBannerIndex++);
  const notificationPosition = bannerTopPosition(nextBannerIndex);
  if (hasNotificationBanner) nextBannerIndex++;
  const demoPosition = bannerTopPosition(nextBannerIndex);
  if (isDemo) nextBannerIndex++;
  const athenaPosition = bannerTopPosition(nextBannerIndex);

  return (
    <BannerContext.Provider value={bannerState}>
      <>
        <div>
          {/* Migration banner — always top */}
          {hasMigrationBanner && (
            <Banner
              position={migrationPosition}
              rightElement={
                <a
                  href={NEW_PLATFORM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-1 bg-white text-[#E34290] text-[10px] sm:text-xs font-semibold rounded hover:bg-white/90 transition-colors"
                >
                  Go to platform.jua.ai →
                </a>
              }
            >
              You're viewing the legacy platform. Please switch before Friday, July 31.
            </Banner>
          )}

          {/* Notification banner */}
          <NotificationBannerManager position={notificationPosition} />

          {/* Demo banner - always at top since notifications are hidden in demo mode */}
          {isDemo && (
            <Banner
              position={demoPosition}
              leftElement={
                <button
                  onClick={() => setDemo(false)}
                  className="normal-case text-[10px] sm:text-xs font-medium px-2 py-1 bg-white text-[#E34290] hover:bg-white/90 rounded transition-colors"
                >
                  Exit demo
                </button>
              }
            >
              Demo - Forecasts from the 16th of August 2025
            </Banner>
          )}

          {/* Athena trial banner - for eligible users */}
          {isAthenaTrialEligible && (
            <Banner
              position={athenaPosition}
              leftElement={<Sparkles className="h-4 w-4" />}
              rightElement={
                <a
                  href={getAthenaUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-1 bg-white text-[#E34290] text-[10px] sm:text-xs font-semibold rounded hover:bg-white/90 transition-colors"
                >
                  Try Athena →
                </a>
              }
              onClose={() => setAthenaTrialDismissed(true)}
            >
              Try out{' '}
              <a
                href={getAthenaUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-2 py-0.5 bg-white text-[#E34290] font-semibold rounded hover:bg-white/90 transition-colors"
              >
                Athena
              </a>{' '}
              with your current subscription until February 6th! Upgrade to keep access after that.
            </Banner>
          )}

          {/* Athena promo banner - for base users */}
          {isAthenaPromoEligible && (
            <Banner
              position={athenaPosition}
              leftElement={<Sparkles className="h-4 w-4" />}
              rightElement={
                <a
                  href="https://jua.ai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-1 bg-white text-[#E34290] text-[10px] sm:text-xs font-semibold rounded hover:bg-white/90 transition-colors"
                >
                  Learn more →
                </a>
              }
              onClose={() => setAthenaPromoDismissed(true)}
            >
              Have you heard of Athena, our AI-powered weather intelligence platform for energy
              trading?
            </Banner>
          )}
        </div>
        <MigrationModal />
        {children}
      </>
    </BannerContext.Provider>
  );
}
